import { useRef, useState } from 'react';
import { ReactComponent as EditSvg } from '../../../../assets/svg/edit.svg';
import { ReactComponent as CloseSvg } from '../../../../assets/svg/closeCircle.svg';
import { ReactComponent as LoadingSvg } from '../../../../assets/svg/loading-half-status.svg';
import { ReactComponent as ChevronUpSvg } from '../../../../assets/svg/chevron-up.svg';
import { ReactComponent as ChevronDownSvg } from '../../../../assets/svg/chevron-down.svg';
import { TrashIcon } from '@heroicons/react/20/solid';
import UpdateDialog from './updateDialog';
import request from '../../../../lib/request';

const bmiColor = [
  {
    bgColor: '#ffc412',
    max: 18.5,
  },
  {
    bgColor: '#13cdc4',
    max: 24.9,
  },
  {
    bgColor: '#1288a9',
    max: 29.9,
  },
  {
    bgColor: '#eb4c68',
    max: 39.9,
  },
];

const getBmiColor = (bmi) => {
  for (const obj of bmiColor) {
    if (obj.max >= bmi) {
      return obj.bgColor;
    }
  }

  return '#000';
};

const bmiValue = (value) => {
  return (
    <div
      className='font-semibold'
      style={{
        color: getBmiColor(value),
      }}
    >
      {value}
    </div>
  );
};

const changeWeightValue = (value) => {
  return (
    <div className='flex items-center gap-2 justify-center'>
      <span>{value.toFixed(2)}</span>
      {Number(value) > 0 ? (
        <ChevronUpSvg
          fill='#ef4444'
          className='size-4'
        />
      ) : Number(value) < 0 ? (
        <ChevronDownSvg
          fill='#3b82f6'
          className='size-4'
        />
      ) : null}
    </div>
  );
};

const LogBook = ({
  tableData,
  openCreateDialog,
  logs,
  refresh,
  preference,
}) => {
  const bottomDialogRef = useRef(null);
  const updateDialogRef = useRef(null);

  const [selectedLog, setSelectedLog] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleDelete = async () => {
    try {
      if (!window.confirm('Are you sure you want to delete this item?')) {
        return;
      }

      setLoading(true);

      await request.delete(`/log/weight/${selectedLog.id}`);

      await refresh();
      closeBottomDialog();
    } catch (error) {
      console.error('Error deleting log:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLog = (index) => () => {
    setSelectedLog(logs.toReversed()[index]);
    openBottomDialog();
  };

  const openBottomDialog = () => {
    if (bottomDialogRef.current) {
      bottomDialogRef.current.showModal();
    }
  };

  const closeBottomDialog = () => {
    if (bottomDialogRef.current) {
      bottomDialogRef.current.close();
    }
  };

  const openUpdateDialog = () => {
    if (updateDialogRef.current) {
      setIsDialogOpen(true);
      updateDialogRef.current.showModal();
      closeBottomDialog();
    }
  };

  const closeUpdateDialog = () => {
    if (updateDialogRef.current) {
      setIsDialogOpen(false);
      updateDialogRef.current.close();
    }
  };

  return (
    <div className='space-y-2 text-white'>
      <div className='flex justify-between items-center'>
        <div className='font-bold'>Log Book</div>
        <button
          className='bg-tertiary rounded-full size-8'
          onClick={openCreateDialog}
        >
          +
        </button>
        <UpdateDialog
          dialogRef={updateDialogRef}
          closeDialog={closeUpdateDialog}
          isDialogOpen={isDialogOpen}
          log={selectedLog}
          refresh={refresh}
          preference={preference}
        />
      </div>
      <table className='w-full text-sm rounded-t-lg overflow-hidden'>
        <thead>
          <tr className='bg-secondary text-xs'>
            {tableData.headers.map((header) => (
              <th
                key={header}
                className='px-4 py-3 text-center font-medium text-white'
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.bodies.toReversed().map((log, i) => (
            <tr
              key={i}
              className='border-b border-tertiary cursor-pointer hover:bg-tertiary/30 transition-colors'
              onClick={handleSelectLog(i)}
            >
              {log.map((value, k) => (
                <td
                  key={k}
                  className='px-2 py-3 text-center'
                >
                  {k === 3
                    ? bmiValue(value)
                    : typeof value === 'number'
                    ? k === 2
                      ? changeWeightValue(value)
                      : value.toFixed(2)
                    : value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <dialog
        ref={bottomDialogRef}
        className='w-full h-full max-w-lg max-h-full bg-transparent'
        onCancel={closeBottomDialog}
      >
        <div
          className={`bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl px-3 py-6 text-white`}
        >
          <div className='mb-4 flex justify-end'>
            <CloseSvg
              className='size-5'
              onClick={closeBottomDialog}
            />
          </div>
          <div className='text-sm space-y-4'>
            <button
              className='px-3 py-2 flex items-center gap-2'
              onClick={openUpdateDialog}
              disabled={loading}
            >
              <EditSvg className='size-5 opacity-40' />
              Edit the log
            </button>
            <button
              className='px-3 py-2 flex items-center gap-2'
              disabled={loading}
              onClick={handleDelete}
            >
              <TrashIcon className='size-5 opacity-40' />
              Delete the log
            </button>
          </div>
          {loading && (
            <div className='absolute animate-bounce inset-x-0 top-4 flex justify-center items-center'>
              <LoadingSvg className='animate-spin' />
            </div>
          )}
        </div>
      </dialog>
    </div>
  );
};

export default LogBook;
